import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherRequisition = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form States for New Requisition
  const [requisitionForm, setRequisitionForm] = useState({
    type: 'stationery',
    title: '',
    description: '',
    justification: '',
    priority: 'medium',
    expectedDate: '',
    estimatedCost: '0.00',
    items: [{
      itemName: '',
      specification: '',
      quantity: '',
      unit: 'pieces',
      estimatedRate: '',
      estimatedAmount: '0.00',
      brandPreference: '',
      remarks: ''
    }]
  });

  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teacherData, setTeacherData] = useState({});
  const [departmentBudget, setDepartmentBudget] = useState({});

  // Dashboard Statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalRequisitions: 0,
    pendingApproval: 0,
    approved: 0,
    rejected: 0,
    inProcess: 0,
    totalValue: '₹0',
    thisMonthSubmitted: 0,
    avgApprovalTime: '0 days',
    budgetAllocated: '₹0',
    budgetUsed: '₹0',
    budgetAvailable: '₹0'
  });

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Units of measurement
  const units = [
    'pieces', 'units', 'boxes', 'reams', 'packets', 'bottles', 'meters',
    'kilograms', 'liters', 'sets', 'hours', 'days', 'months', 'licenses', 'other'
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Update dashboard stats when requisitions change
  useEffect(() => {
    updateDashboardStats();
  }, [requisitions]);

  // API call helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/teacher${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleLogout();
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all required data
      const [
        teacherResponse,
        categoriesResponse,
        requisitionsResponse,
        budgetResponse
      ] = await Promise.all([
        apiCall('/profile'),
        apiCall('/procurement/categories'),
        apiCall('/procurement/requisitions'),
        apiCall('/procurement/budget')
      ]);

      if (teacherResponse.success) {
        setTeacherData(teacherResponse.data);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }

      if (requisitionsResponse.success) {
        setRequisitions(requisitionsResponse.data);
      }

      if (budgetResponse.success) {
        setDepartmentBudget(budgetResponse.data);
      }

    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load data: ' + error.message);
      showNotification('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update dashboard statistics
  const updateDashboardStats = () => {
    const stats = {
      totalRequisitions: requisitions.length,
      pendingApproval: requisitions.filter(r =>
        r.status === 'hod_pending' || r.status === 'admin_pending' || r.status === 'submitted'
      ).length,
      approved: requisitions.filter(r =>
        r.status === 'hod_approved' || r.status === 'admin_approved' || r.status === 'approved'
      ).length,
      rejected: requisitions.filter(r =>
        r.status === 'hod_rejected' || r.status === 'admin_rejected' || r.status === 'rejected'
      ).length,
      inProcess: requisitions.filter(r =>
        r.status === 'in_procurement' || r.status === 'tender_created'
      ).length,
      totalValue: formatCurrency(requisitions.reduce((sum, r) => sum + (parseFloat(r.estimated_cost) || 0), 0)),
      thisMonthSubmitted: requisitions.filter(r => {
        const date = new Date(r.submitted_at || r.created_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }).length,
      avgApprovalTime: calculateAvgApprovalTime(),
      budgetAllocated: formatCurrency(departmentBudget.allocated_budget || 0),
      budgetUsed: formatCurrency(departmentBudget.used_budget || 0),
      budgetAvailable: formatCurrency(departmentBudget.available_budget || 0)
    };
    setDashboardStats(stats);
  };

  // Calculate average approval time
  const calculateAvgApprovalTime = () => {
    const approvedReqs = requisitions.filter(r =>
      (r.status === 'approved' || r.status === 'admin_approved') && r.submitted_at && r.admin_approved_at
    );

    if (approvedReqs.length === 0) return '0 days';

    const totalDays = approvedReqs.reduce((sum, req) => {
      const submitted = new Date(req.submitted_at);
      const approved = new Date(req.admin_approved_at);
      const days = Math.ceil((approved - submitted) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0);

    const avgDays = Math.round(totalDays / approvedReqs.length);
    return `${avgDays} day${avgDays !== 1 ? 's' : ''}`;
  };



  // Handle form input changes
  const handleInputChange = (field, value) => {
    setRequisitionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const newItems = [...requisitionForm.items];
    newItems[index][field] = value;

    // Auto-calculate estimated amount when quantity or rate changes
    if (field === 'quantity' || field === 'estimatedRate') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].estimatedRate) || 0;
      newItems[index].estimatedAmount = (quantity * rate).toFixed(2);
    }

    setRequisitionForm(prev => ({
      ...prev,
      items: newItems
    }));

    // Update total estimated cost
    updateTotalCost(newItems);
  };

  // Update total estimated cost
  const updateTotalCost = (items) => {
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.estimatedAmount) || 0);
    }, 0);
    setRequisitionForm(prev => ({
      ...prev,
      estimatedCost: total.toFixed(2)
    }));
  };

  // Add new item
  const addItem = () => {
    setRequisitionForm(prev => ({
      ...prev,
      items: [...prev.items, {
        itemName: '',
        specification: '',
        quantity: '',
        unit: 'pieces',
        estimatedRate: '',
        estimatedAmount: '0.00',
        brandPreference: '',
        remarks: ''
      }]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (requisitionForm.items.length > 1) {
      const newItems = requisitionForm.items.filter((_, i) => i !== index);
      setRequisitionForm(prev => ({
        ...prev,
        items: newItems
      }));
      updateTotalCost(newItems);
    }
  };

  // Submit requisition
  const submitRequisition = async () => {
    setLoading(true);

    try {
      // Validation
      if (!requisitionForm.title.trim()) {
        throw new Error('Please enter a title for your requisition');
      }

      if (!requisitionForm.description.trim()) {
        throw new Error('Please enter a description for your requisition');
      }

      if (!requisitionForm.justification.trim()) {
        throw new Error('Please provide justification for this requisition');
      }

      const validItems = requisitionForm.items.filter(item =>
        item.itemName.trim() && item.quantity.trim() && item.estimatedRate.trim()
      );

      if (validItems.length === 0) {
        throw new Error('Please add at least one valid item with name, quantity, and estimated rate');
      }

      // Check budget availability
      const requestedAmount = parseFloat(requisitionForm.estimatedCost);
      const availableBudget = parseFloat(departmentBudget.available_budget || 0);

      if (requestedAmount > availableBudget) {
        throw new Error(`Requested amount (${formatCurrency(requestedAmount)}) exceeds available budget (${formatCurrency(availableBudget)})`);
      }

      // Prepare submission data
      const submissionData = {
        title: requisitionForm.title.trim(),
        description: requisitionForm.description.trim(),
        justification: requisitionForm.justification.trim(),
        category_code: requisitionForm.type,
        priority: requisitionForm.priority,
        expected_delivery_date: requisitionForm.expectedDate || null,
        estimated_cost: parseFloat(requisitionForm.estimatedCost),
        items: validItems.map(item => ({
          item_name: item.itemName.trim(),
          specification: item.specification.trim(),
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          estimated_rate: parseFloat(item.estimatedRate),
          estimated_amount: parseFloat(item.estimatedAmount),
          brand_preference: item.brandPreference?.trim() || null,
          remarks: item.remarks?.trim() || null
        }))
      };

      // Submit to API
      const response = await apiCall('/procurement/requisitions', {
        method: 'POST',
        body: JSON.stringify(submissionData)
      });

      if (response.success) {
        // Reset form
        setRequisitionForm({
          type: 'stationery',
          title: '',
          description: '',
          justification: '',
          priority: 'medium',
          expectedDate: '',
          estimatedCost: '0.00',
          items: [{
            itemName: '',
            specification: '',
            quantity: '',
            unit: 'pieces',
            estimatedRate: '',
            estimatedAmount: '0.00',
            brandPreference: '',
            remarks: ''
          }]
        });

        // Refresh data
        await fetchInitialData();

        showNotification('✅ Requisition submitted successfully! It will be forwarded to HOD for approval.', 'success');
        setActiveTab('my-requisitions');
      }

    } catch (error) {
      console.error('Error submitting requisition:', error);
      showNotification('❌ ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type
    };

    setNotifications(prev => [...prev, notification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'admin_approved':
      case 'hod_approved':
        return '#28a745';
      case 'rejected':
      case 'admin_rejected':
      case 'hod_rejected':
        return '#dc3545';
      case 'hod_pending':
        return '#ffc107';
      case 'admin_pending':
      case 'submitted':
        return '#17a2b8';
      case 'in_procurement':
      case 'tender_created':
        return '#6f42c1';
      default:
        return '#6c757d';
    }
  };

  // Get status background color
  const getStatusBgColor = (status) => {
    switch (status) {
      case 'approved':
      case 'admin_approved':
      case 'hod_approved':
        return '#d4edda';
      case 'rejected':
      case 'admin_rejected':
      case 'hod_rejected':
        return '#f8d7da';
      case 'hod_pending':
        return '#fff3cd';
      case 'admin_pending':
      case 'submitted':
        return '#d1ecf1';
      case 'in_procurement':
      case 'tender_created':
        return '#e2d9f3';
      default:
        return '#e9ecef';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'submitted': return 'Submitted';
      case 'hod_pending': return 'HOD Review';
      case 'hod_approved': return 'HOD Approved';
      case 'hod_rejected': return 'HOD Rejected';
      case 'admin_pending': return 'Admin Review';
      case 'admin_approved': return 'Admin Approved';
      case 'admin_rejected': return 'Admin Rejected';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'tender_created': return 'Tender Created';
      case 'in_procurement': return 'In Procurement';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  // Get category label
  const getCategoryLabel = (code) => {
    const category = categories.find(cat => cat.category_code === code);
    return category ? `${category.category_icon} ${category.category_name}` : code;
  };

  // Loading state
  if (loading && requisitions.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Procurement Portal...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-requisition" userInfo={userInfo}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
      {/* Error Display */}
      {error && (
        <div style={{
          background: '#ffebee',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>❌</span>
          {error}
        </div>
      )}

      {/* Page Header */}
      <div style={{
        background: '#ffffff',
        padding: isMobile ? '1rem' : '2rem',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        textAlign: 'center'
      }}>
        <h1 style={{
          margin: isMobile ? '0 0 0.5rem 0' : '0 0 1rem 0',
          color: '#333',
          fontSize: isMobile ? '1.3rem' : '2.2rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '0.5rem' : '1rem'
        }}>
          <span style={{ fontSize: isMobile ? '1.5rem' : '2.5rem' }}>🏭</span>
          E-Procurement Portal
        </h1>
        <p style={{
          margin: 0,
          color: '#666',
          fontSize: isMobile ? '0.8rem' : '1.1rem'
        }}>
          Supply Management & Requisition System
        </p>
        <div style={{
          marginTop: isMobile ? '0.8rem' : '1rem',
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '1.2rem' : '2rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', color: '#238548' }}>
              {dashboardStats.totalRequisitions}
            </div>
            <div style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: '#666' }}>Total Requisitions</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
              {dashboardStats.pendingApproval}
            </div>
            <div style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: '#666' }}>Pending Approval</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
              {dashboardStats.approved}
            </div>
            <div style={{ fontSize: isMobile ? '0.7rem' : '0.9rem', color: '#666' }}>Approved</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e9ecef',
          overflowX: 'auto'
        }}>
          {[
            { key: 'dashboard', label: 'Dashboard', icon: '📊' },
            { key: 'new-requisition', label: 'New Requisition', icon: '➕' },
            { key: 'my-requisitions', label: 'My Requisitions', icon: '📋' },
            { key: 'budget-tracker', label: 'Budget Tracker', icon: '💰' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: isMobile ? '0.7rem 0.3rem' : '1.2rem',
                border: 'none',
                background: activeTab === tab.key ? '#238548' : 'transparent',
                color: activeTab === tab.key ? '#ffffff' : '#666',
                fontSize: isMobile ? '0.7rem' : '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '0.2rem' : '0.5rem',
                minWidth: isMobile ? 'unset' : '150px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📊</span>
                Procurement Dashboard
              </h3>

              {/* Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                gap: isMobile ? '0.75rem' : '1.5rem',
                marginBottom: isMobile ? '1rem' : '2rem'
              }}>
                {[
                  { label: 'Total Value', value: dashboardStats.totalValue, color: '#238548', icon: '💰' },
                  { label: 'This Month', value: dashboardStats.thisMonthSubmitted, color: '#17a2b8', icon: '📈' },
                  { label: 'In Process', value: dashboardStats.inProcess, color: '#6f42c1', icon: '⚙️' },
                  { label: 'Avg Approval', value: dashboardStats.avgApprovalTime, color: '#fd7e14', icon: '⏱️' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #e9ecef',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                    <div style={{
                      fontSize: isMobile ? '1.3rem' : '2rem',
                      marginBottom: isMobile ? '0.3rem' : '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '1.1rem' : '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      marginBottom: isMobile ? '0.2rem' : '0.5rem'
                    }}>
                      {stat.value}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.65rem' : '0.9rem',
                      color: '#666'
                    }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Budget Overview */}
              {departmentBudget && (
                <div style={{
                  background: '#f8f9fa',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '2rem'
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Department Budget Overview</h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '1rem'
                  }}>
                    <div style={{
                      background: '#ffffff',
                      padding: '1rem',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#238548' }}>
                      </div>
                    </div>
                    <div style={{
                      background: '#ffffff',
                      padding: '1rem',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545' }}>
                        {dashboardStats.budgetUsed}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Used Budget</div>
                    </div>
                    <div style={{
                      background: '#ffffff',
                      padding: '1rem',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '8px'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Recent Activity</h4>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {requisitions.length > 0 ? (
                    requisitions.slice(0, 3).map((req) => (
                      <div key={req.requisition_id} style={{
                        background: '#ffffff',
                        padding: '1rem',
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #e9ecef'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500', color: '#333' }}>{req.title}</div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            {req.requisition_id} • {formatDate(req.submitted_at || req.created_at)}
                          </div>
                        </div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          backgroundColor: getStatusBgColor(req.status),
                          color: getStatusColor(req.status)
                        }}>
                          {getStatusLabel(req.status)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: '#666'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        marginBottom: '1rem',
                        opacity: 0.5
                      }}>
                        📋
                      </div>
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        No recent activity. Submit your first requisition to get started.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* New Requisition Tab */}
          {activeTab === 'new-requisition' && (
            <div>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>➕</span>
                Create New Requisition
              </h3>

              {/* Basic Information */}
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Basic Information</h4>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Procurement Category *
                    </label>
                    <select
                      value={requisitionForm.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    >
                      {categories.map((cat) => (
                        <option key={cat.category_code} value={cat.category_code}>
                          {cat.category_icon} {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Priority Level *
                    </label>
                    <select
                      value={requisitionForm.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="low">🟢 Low Priority</option>
                      <option value="medium">🟡 Medium Priority</option>
                      <option value="high">🔴 High Priority</option>
                      <option value="urgent">🚨 Urgent</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Requisition Title *
                  </label>
                  <input
                    type="text"
                    value={requisitionForm.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter a clear, descriptive title for your requisition"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Description *
                  </label>
                  <textarea
                    value={requisitionForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Provide detailed description of your procurement needs"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Business Justification *
                  </label>
                  <textarea
                    value={requisitionForm.justification}
                    onChange={(e) => handleInputChange('justification', e.target.value)}
                    placeholder="Explain why this procurement is needed and how it benefits the department/students"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Expected Delivery Date
                    </label>
                    <input
                      type="date"
                      value={requisitionForm.expectedDate}
                      onChange={(e) => handleInputChange('expectedDate', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Total Estimated Cost
                    </label>
                    <input
                      type="number"
                      value={requisitionForm.estimatedCost}
                      readOnly
                      placeholder="Auto-calculated from items"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: '#f8f9fa'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ margin: 0, color: '#333' }}>Items Required</h4>
                  <button
                    onClick={addItem}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#1e7139';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#238548';
                    }}
                  >
                    ➕ Add Item
                  </button>
                </div>

                {requisitionForm.items.map((item, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <h5 style={{ margin: 0, color: '#333' }}>Item {index + 1}</h5>
                      {requisitionForm.items.length > 1 && (
                        <button
                          onClick={() => removeItem(index)}
                          style={{
                            background: '#dc3545',
                            color: '#ffffff',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Item Name *
                        </label>
                        <input
                          type="text"
                          value={item.itemName}
                          onChange={(e) => handleItemChange(index, 'itemName', e.target.value)}
                          placeholder="Enter item name"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Quantity *
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="e.g., 10"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Unit
                        </label>
                        <select
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        >
                          {units.map(unit => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Estimated Rate (₹) *
                        </label>
                        <input
                          type="number"
                          value={item.estimatedRate}
                          onChange={(e) => handleItemChange(index, 'estimatedRate', e.target.value)}
                          placeholder="Rate per unit"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Estimated Amount (₹)
                        </label>
                        <input
                          type="number"
                          value={item.estimatedAmount}
                          readOnly
                          placeholder="Auto-calculated"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            backgroundColor: '#f8f9fa'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Brand Preference
                        </label>
                        <input
                          type="text"
                          value={item.brandPreference}
                          onChange={(e) => handleItemChange(index, 'brandPreference', e.target.value)}
                          placeholder="Optional brand"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Technical Specifications
                      </label>
                      <textarea
                        value={item.specification}
                        onChange={(e) => handleItemChange(index, 'specification', e.target.value)}
                        placeholder="Detailed technical specifications, quality requirements, etc."
                        rows="2"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Remarks
                      </label>
                      <input
                        type="text"
                        value={item.remarks}
                        onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                        placeholder="Optional notes"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit Section */}
              <div style={{
                background: '#e8f5e9',
                padding: '1.5rem',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#2e7d32' }}>
                  🔄 Approval Flow Preview
                </h4>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: '#28a745',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}>
                    1. Teacher (You)
                  </div>
                  <span style={{ fontSize: '1.5rem', color: '#28a745' }}>→</span>
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: '#ffc107',
                    color: '#333',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}>
                    2. HOD Approval
                  </div>
                  <span style={{ fontSize: '1.5rem', color: '#ffc107' }}>→</span>
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: '#17a2b8',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}>
                    3. Admin Approval
                  </div>
                </div>

                {/* Budget Check */}
                {departmentBudget && (
                  <div style={{
                    background: '#ffffff',
                    padding: '1rem',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    fontSize: '0.9rem',
                    color: '#333'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Budget Check:</div>
                    <div>Available Budget: <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                      {formatCurrency(departmentBudget.available_budget || 0)}
                    </span></div>
                    <div>Requested Amount: <span style={{
                      color: parseFloat(requisitionForm.estimatedCost) > parseFloat(departmentBudget.available_budget || 0) ? '#dc3545' : '#238548',
                      fontWeight: 'bold'
                    }}>
                      {formatCurrency(requisitionForm.estimatedCost)}
                    </span></div>
                    {parseFloat(requisitionForm.estimatedCost) > parseFloat(departmentBudget.available_budget || 0) && (
                      <div style={{ color: '#dc3545', fontWeight: 'bold', marginTop: '0.5rem' }}>
                        ⚠️ Requested amount exceeds available budget!
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={submitRequisition}
                  disabled={loading}
                  style={{
                    background: loading ? '#6c757d' : '#238548',
                    color: '#ffffff',
                    border: 'none',
                    padding: '1rem 2rem',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0 auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.background = '#1e7139';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.background = '#238548';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Submitting Requisition...
                    </>
                  ) : (
                    <>
                      <span>🚀</span>
                      Submit for HOD Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* My Requisitions Tab */}
          {activeTab === 'my-requisitions' && (
            <div>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📋</span>
                My Requisition History
              </h3>

              {requisitions.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gap: '1.5rem'
                }}>
                  {requisitions.map((req) => (
                    <div key={req.requisition_id} style={{
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      transition: 'all 0.3s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.2rem' }}>
                            {req.title}
                          </h4>
                          <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                            ID: {req.requisition_id} • Category: {getCategoryLabel(req.category_code)}
                          </p>
                          <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                            {req.description}
                          </p>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            Estimated Cost: <strong>{formatCurrency(req.estimated_cost)}</strong>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            backgroundColor: getStatusBgColor(req.status),
                            color: getStatusColor(req.status),
                            textTransform: 'capitalize'
                          }}>
                            {getStatusLabel(req.status)}
                          </span>
                          <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.8rem',
                            color: '#999'
                          }}>
                            Submitted: {formatDate(req.submitted_at || req.created_at)}
                          </div>
                          {(req.hod_rejection_reason || req.admin_rejection_reason) && (
                            <div style={{
                              marginTop: '0.5rem',
                              fontSize: '0.8rem',
                              color: '#dc3545',
                              fontWeight: '500'
                            }}>
                              Reason: {req.hod_rejection_reason || req.admin_rejection_reason}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Items List */}
                      {req.items && req.items.length > 0 && (
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1rem',
                          borderRadius: '6px'
                        }}>
                          <h5 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.9rem' }}>
                            Items Requested:
                          </h5>
                          {req.items.map((item, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0.25rem 0',
                              borderBottom: index < req.items.length - 1 ? '1px solid #e9ecef' : 'none',
                              fontSize: '0.9rem'
                            }}>
                              <span style={{ color: '#333', flex: 1 }}>
                                {item.item_name}
                                {item.specification && (
                                  <span style={{ color: '#666', fontSize: '0.8rem' }}>
                                    {' - ' + item.specification.substring(0, 50) + (item.specification.length > 50 ? '...' : '')}
                                  </span>
                                )}
                              </span>
                              <span style={{ color: '#666', fontSize: '0.8rem', minWidth: '120px', textAlign: 'right' }}>
                                {item.quantity} {item.unit} @ ₹{item.estimated_rate} = {formatCurrency(item.estimated_amount)}
                              </span>
                            </div>
                          ))}
                          <div style={{
                            marginTop: '0.5rem',
                            paddingTop: '0.5rem',
                            borderTop: '2px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontWeight: 'bold'
                          }}>
                            <span>Total Estimated Cost:</span>
                            <span style={{ color: '#238548' }}>{formatCurrency(req.estimated_cost)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#666'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem',
                    opacity: 0.5
                  }}>
                    📋
                  </div>
                  <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    No requisitions found
                  </p>
                  <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                    Submit your first requisition to see it here.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Budget Tracker Tab */}
          {activeTab === 'budget-tracker' && (
            <div>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>💰</span>
                Budget Tracker
              </h3>

              {/* Budget Overview */}
              {departmentBudget ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  {[
                    {
                      label: 'Budget Used',
                      value: formatCurrency(departmentBudget.used_budget),
                      color: '#dc3545',
                      icon: '📊'
                    },
                  ].map((stat, index) => (
                    <div key={index} style={{
                      background: '#ffffff',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: '1px solid #e9ecef',
                      transition: 'all 0.3s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                      <div style={{
                        fontSize: '2rem',
                        marginBottom: '0.5rem'
                      }}>
                        {stat.icon}
                      </div>
                      <div style={{
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        color: stat.color,
                        marginBottom: '0.5rem'
                      }}>
                        {stat.value}
                      </div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#666'
                      }}>
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#666'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    marginBottom: '1rem',
                    opacity: 0.5
                  }}>
                    💰
                  </div>
                  <p>Budget information not available</p>
                </div>
              )}

              {/* Budget Information */}
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '8px'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Budget Guidelines:</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '1rem'
                }}>
                  {[
                    {
                      title: 'Budget Planning',
                      desc: 'Plan your requisitions according to departmental budget allocation',
                      icon: '📋',
                      color: '#238548'
                    },
                    {
                      title: 'Approval Process',
                      desc: 'All requisitions require HOD and Admin approval for budget compliance',
                      icon: '✅',
                      color: '#ffc107'
                    },
                    {
                      title: 'Priority Guidelines',
                      desc: 'Urgent requirements get priority in budget allocation',
                      icon: '🚨',
                      color: '#dc3545'
                    },
                    {
                      title: 'Quarterly Review',
                      desc: 'Budget utilization is reviewed quarterly for optimization',
                      icon: '📊',
                      color: '#17a2b8'
                    }
                  ].map((item, index) => (
                    <div key={index} style={{
                      padding: '1rem',
                      background: '#ffffff',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                        <span style={{
                          fontWeight: '500',
                          color: item.color
                        }}>
                          {item.title}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#666',
                        lineHeight: 1.4
                      }}>
                        {item.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999
      }}>
        {notifications.map(notification => (
          <div
            key={notification.id}
            style={{
              maxWidth: '350px',
              padding: '0',
              borderRadius: '4px',
              boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
              marginBottom: '10px',
              overflow: 'hidden',
              animation: 'slideIn 0.3s ease-out forwards',
              backgroundColor:
                notification.type === 'success' ? '#e8f5e9' :
                  notification.type === 'warning' ? '#fff3e0' :
                    notification.type === 'error' ? '#ffebee' : '#e3f2fd',
              borderLeft: `4px solid ${notification.type === 'success' ? '#4caf50' :
                notification.type === 'warning' ? '#ff9800' :
                  notification.type === 'error' ? '#f44336' : '#2196f3'
                }`
            }}
          >
            <div style={{
              padding: '15px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{
                color:
                  notification.type === 'success' ? '#1b5e20' :
                    notification.type === 'warning' ? '#e65100' :
                      notification.type === 'error' ? '#b71c1c' : '#0d47a1'
              }}>
                {notification.message}
              </span>
              <button
                onClick={() => removeNotification(notification.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  padding: 0
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
};

export default TeacherRequisition;